/**
 * WhatIfResultCard – Displays a single KPI delta as a compact before/after card.
 */
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import type { WhatIfKpiDelta } from '@/modules/shared/services/whatIfApiService';

interface WhatIfResultCardProps {
  delta: WhatIfKpiDelta;
}

export function WhatIfResultCard({ delta }: WhatIfResultCardProps) {
  const directionIcon = {
    UP: <ArrowUpRight className="w-3 h-3 text-emerald-500" />,
    DOWN: <ArrowDownRight className="w-3 h-3 text-red-500" />,
    FLAT: <Minus className="w-3 h-3 text-muted-foreground" />,
  }[delta.direction];

  const directionColor = {
    UP: 'text-emerald-600',
    DOWN: 'text-red-600',
    FLAT: 'text-muted-foreground',
  }[delta.direction];

  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-foreground truncate">{delta.label}</span>
        <div className="flex items-center gap-0.5">{directionIcon}</div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Baseline</span>
          <p className="text-[12px] font-medium text-foreground">{delta.baselineValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
        </div>
        <div>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Simulated</span>
          <p className={`text-[12px] font-medium ${directionColor}`}>{delta.simulatedValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-border">
        <span className="text-[11px] text-muted-foreground">Change</span>
        <span className={`text-[11px] font-medium ${directionColor}`}>
          {delta.percentChange > 0 ? '+' : ''}{delta.percentChange}%
        </span>
      </div>
    </div>
  );
}

